import React, { useEffect, useMemo, useRef, useState } from "react";
import { useMutation } from "@apollo/client";
import { ChartData, ChartOptions } from "chart.js";
import { Line } from "react-chartjs-2";
import { MUTATE_RUN_RESS_RACE_BUILDER } from "../../utils/graphql/queries/ressQueries";
import { RaceBuilderFormState } from "./RaceBuilderInputs";
import { RaceProfileDraft } from "./RaceProfile";
import { RessEnergySelection, RessRunDataset } from "../../types/ress";
import { EnergySelectionResults } from "./EnergySelectionGraphs";
import styles from "./style.module.scss";

type SimulationData = {
  tCellMaxC: number[];
  packPowerkW: number[];
  pbmsKw: number[];
  pitBoostLapIndex: number | null;
};

type SimulationResult = {
  data: SimulationData;
  metadata: {
    dataset: RessRunDataset;
    energySelection: RessEnergySelection | null;
    ts: {
      queryReceived: number;
      dataLoaded: number;
      raceBuilt: number;
      fmuRan: number;
      result: number;
    };
  };
};

type SimulationRun = SimulationResult & {
  inputs: RaceBuilderFormState;
  profile: RaceProfileDraft;
  pitBoost: PitBoostConfig;
};

type PitBoostConfig = {
  pitStopTimeS: number | null;
  segmentEndSlapM: number[] | null;
};

export function useRaceBuilderSimulation(
  inputs: RaceBuilderFormState,
  profile: RaceProfileDraft | null,
  strategyId: string | null,
  pitBoost: PitBoostConfig,
) {
  const [runModel, { loading }] = useMutation<{
    runRessModel: SimulationResult | null;
  }>(MUTATE_RUN_RESS_RACE_BUILDER, {
    fetchPolicy: "no-cache",
    context: { skipRetry: true },
  });
  const [result, setResult] = useState<SimulationRun | null>(null);
  const [error, setError] = useState<string | null>(null);
  const requestVersion = useRef(0);
  const running = useRef(false);
  const profileChanged =
    profile !== null &&
    (profile.dataFile !== inputs.dataFile.trim() ||
      profile.totalRaceEnergykWh !== inputs.totalRaceEnergykWh ||
      profile.numberOfLaps !== inputs.numberOfLaps);
  const canRun =
    profile !== null &&
    !profileChanged &&
    inputs.dataFile.trim() !== "" &&
    Object.entries(inputs).every(
      ([name, value]) =>
        (name === "targetPitSoc" && !inputs.pitBoostRace) || value !== "",
    );

  useEffect(() => {
    requestVersion.current += 1;
    setResult(null);
    setError(null);
    return () => {
      requestVersion.current += 1;
    };
  }, [strategyId]);

  async function run() {
    if (running.current || !canRun || !profile) return;
    const version = ++requestVersion.current;
    running.current = true;
    setError(null);
    setResult(null);

    try {
      const {
        totalRaceEnergykWh,
        numberOfLaps,
        pitBoostRace,
        targetPitSoc,
        pitScenario,
        ...modelInputs
      } = inputs;
      for (const [name, value] of Object.entries(modelInputs)) {
        if (name !== "dataFile" && !Number.isFinite(value)) {
          throw new Error(`'${name}' must be a finite number`);
        }
      }
      if (pitBoostRace) {
        if (
          targetPitSoc === "" ||
          !Number.isFinite(targetPitSoc) ||
          targetPitSoc < 0 ||
          targetPitSoc > 100
        ) {
          throw new Error("Target boost SoC must be between 0 and 100%");
        }
        if (pitBoost.pitStopTimeS === null) {
          throw new Error(
            "The selected pit boost scenario has no valid imported stop time",
          );
        }
        if (pitBoost.segmentEndSlapM === null) {
          throw new Error(
            "Pit boost requires valid segment distances from the EM import",
          );
        }
      }
      const response = await runModel({
        variables: {
          inputs: {
            ...modelInputs,
            ...(pitBoostRace
              ? {
                  pitBoostRace: 1,
                  pitStopTimeS: pitBoost.pitStopTimeS,
                  hasPitted: false,
                  targetPitSoc,
                  pitBoostSocSource: "model",
                  segmentEndSlapM: pitBoost.segmentEndSlapM,
                }
              : { pitBoostRace: 0 }),
            dataFile: inputs.dataFile.trim(),
            tow: false,
            towPerLap: profile.laps.map((lap) => lap.tow),
            attackMode: profile.laps.map((lap) => (lap.attack ? 1 : 0)),
            targetEnergykWh: profile.laps.map((lap) => lap.energyTargetkWh),
          },
        },
      });
      if (version !== requestVersion.current) return;
      const modelResult = response.data?.runRessModel;
      if (
        !modelResult?.data.tCellMaxC.length ||
        !modelResult.data.pbmsKw.length
      ) {
        throw new Error("The RESS service returned no model trace data");
      }
      setResult({ ...modelResult, inputs, profile, pitBoost });
    } catch (reason) {
      if (version !== requestVersion.current) return;
      setError(
        `RESS run failed: ${
          reason instanceof Error ? reason.message : "Unknown service error"
        }`,
      );
    } finally {
      running.current = false;
    }
  }

  return {
    run,
    loading,
    canRun,
    error,
    result,
    inputsChanged:
      result !== null &&
      (result.inputs !== inputs ||
        result.profile !== profile ||
        (result.inputs.pitBoostRace &&
          (result.pitBoost.pitStopTimeS !== pitBoost.pitStopTimeS ||
            JSON.stringify(result.pitBoost.segmentEndSlapM) !==
              JSON.stringify(pitBoost.segmentEndSlapM)))),
  };
}

function chartOptions(title: string, yLabel: string): ChartOptions<"line"> {
  return {
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    parsing: false,
    normalized: true,
    interaction: { mode: "nearest", axis: "x", intersect: false },
    plugins: {
      datalabels: { display: false },
      legend: { labels: { color: "#ffffff" } },
      title: { display: true, text: title, color: "#ffffff" },
    },
    scales: {
      x: {
        type: "linear",
        title: { display: true, text: "Time (s)", color: "#ffffff" },
        ticks: { color: "#ffffff" },
        grid: { color: "#505050" },
      },
      y: {
        title: { display: true, text: yLabel, color: "#ffffff" },
        ticks: { color: "#ffffff" },
        grid: { color: "#505050" },
      },
    },
  };
}

const temperatureOptions = chartOptions(
  "Race Builder TCell Max",
  "Temperature (°C)",
);
const powerOptions = chartOptions("Race Builder PBMS", "PBMS (kW)");
// Both RESS service execution paths return samples at 0.1-second intervals.
const MODEL_DT_S = 0.1;

function trace(label: string, values: number[], colour: string) {
  return {
    label,
    data: values.map((value, index) => ({ x: index * MODEL_DT_S, y: value })),
    borderColor: colour,
    backgroundColor: colour,
    borderWidth: 2,
    pointRadius: 0,
    pointHoverRadius: 0,
    tension: 0,
  };
}

type RaceBuilderSimulationProps = {
  simulation: ReturnType<typeof useRaceBuilderSimulation>;
  tCellMaxDerateStarts: number | null;
  disabled: boolean;
};

export default function RaceBuilderSimulation({
  simulation,
  tCellMaxDerateStarts,
  disabled,
}: RaceBuilderSimulationProps) {
  const { result, loading, error, canRun, inputsChanged, run } = simulation;
  const data = result?.data;
  const temperatureData = useMemo<
    ChartData<"line", { x: number; y: number }[]>
  >(
    () => ({
      datasets: [
        {
          ...trace("TCell Max", data?.tCellMaxC ?? [], "#a18b6b"),
          segment: {
            borderColor: (context) =>
              tCellMaxDerateStarts !== null &&
              (context.p0.parsed.y > tCellMaxDerateStarts ||
                context.p1.parsed.y > tCellMaxDerateStarts)
                ? "#ff3b3b"
                : "#a18b6b",
          },
        },
        ...(tCellMaxDerateStarts === null
          ? []
          : [
              {
                ...trace(`${tCellMaxDerateStarts}°C limit`, [], "#ff3b3b"),
                data: [
                  { x: 0, y: tCellMaxDerateStarts },
                  {
                    x: Math.max(
                      ((data?.tCellMaxC.length ?? 0) - 1) * MODEL_DT_S,
                      1,
                    ),
                    y: tCellMaxDerateStarts,
                  },
                ],
                borderDash: [6, 6],
              },
            ]),
      ],
    }),
    [data, tCellMaxDerateStarts],
  );
  const powerData = useMemo(
    () => ({ datasets: [trace("PBMS", data?.pbmsKw ?? [], "#6fa8dc")] }),
    [data],
  );

  return (
    <section
      className={styles.simulationPanel}
      aria-labelledby="race-simulation-title"
      aria-busy={loading}
    >
      <div className={styles.simulationHeader}>
        <h2 id="race-simulation-title">Simulation</h2>
        <button
          type="button"
          className={styles.profileButton}
          disabled={disabled || loading || !canRun}
          onClick={run}
        >
          {loading ? "Running..." : "Run Simulation"}
        </button>
      </div>
      {error && (
        <div className={styles.errorMessage} role="alert">
          {error}
        </div>
      )}
      <div className={styles.simulationStatus} role="status">
        {loading ? "Running RESS simulation..." : null}
        {result?.inputs.pitBoostRace ? (
          <p className={styles.profileHint}>
            {data?.pitBoostLapIndex != null
              ? `Pit boost after lap ${data.pitBoostLapIndex + 1}.`
              : "No qualifying pit boost lap found."}
          </p>
        ) : null}
        {inputsChanged ? (
          <p className={styles.profileHint}>
            Inputs or lap selections have changed. Run the simulation again to
            update the plots.
          </p>
        ) : null}
      </div>
      <div className={styles.simulationCharts}>
        <div className={styles.chartCanvas}>
          <Line
            data={temperatureData}
            options={temperatureOptions}
            role="img"
            aria-label="Race Builder TCell max against time"
          />
        </div>
        <div className={styles.chartCanvas}>
          <Line
            data={powerData}
            options={powerOptions}
            role="img"
            aria-label="Race Builder PBMS against time"
          />
        </div>
      </div>
      <EnergySelectionResults
        selection={result?.metadata.energySelection}
        title="Race Builder energy"
        description={result?.metadata.dataset.fileName}
        showDelta={false}
        emptyMessage={
          result
            ? "Energy selection data was not returned for this Race Builder run."
            : "Run a Race Builder simulation to view lap energy results."
        }
      />
    </section>
  );
}
